import { ModalComponent } from '../../../ui/modal-component/modal-component.component';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import {
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  Output,
  OnDestroy,
} from '@angular/core';
import { FormWrapperComponent } from '../../../ui/form-wrapper/form-wrapper.component';
import { InputFieldComponent } from '../../../ui/inputField/inputField.component';
import { BkSelectComponent } from '../../../inputs/bk-select/bk-select.component';
import {
  FormBuilder,
  ReactiveFormsModule,
  Validators,
  FormArray,
} from '@angular/forms';
import { MessageAttributeMenuComponent } from '../../../ui/message-attribute-menu/message-attribute-menu.component';
import { MessageAttributeChipsComponent } from '../../../ui/message-attribute-chips/message-attribute-chips.component';
import { MessagesService } from '../../../../services/campaign/messages.service';
import { ActivatedRoute, Router } from '@angular/router';
import { SavedMessages } from '../../../../services/campaign/messages.service';
import { removeMessageAttribute } from '../../../../../utils/utils';
import { SmsParts, describeChar, formatCount, smsParts } from '../../../../../utils/sms';
import { WizardAudienceService } from '../../../../pages/organization/campaigns/create-campaign/wizard-audience.service';
import { BroadcastMessageService } from '../../../../services/broadcast/broadcast.service';
import { ISenderID } from '../../../../services/broadcast/types/broadcastTypes';

interface MessagesForm {
  senderId: string | null;
  subject: string | null;
  body: string | null;
  hashtags: string[];
  hashtag: string | null;
}

@Component({
  selector: 'app-edit-message-form',
  standalone: true,
  imports: [
    FormWrapperComponent,
    ModalComponent,
    InputFieldComponent,
    BkSelectComponent,
    ReactiveFormsModule,
    MessageAttributeMenuComponent,
    MessageAttributeChipsComponent,
  ],
  templateUrl: './edit-message-form.component.html',
  styleUrl: '../campaign-form.css',
})
export class EditMessageFormComponent implements OnDestroy {
  @Output() closePopupEvent = new EventEmitter<boolean>();
  @Output() showTestMessage = new EventEmitter();
  @Input({ required: true }) open: boolean = false;
  @Input() msgNo: string | number = 1;
  messagesService = inject(MessagesService);
  broadcastService = inject(BroadcastMessageService);
  audience = inject(WizardAudienceService);
  private destroyRef = inject(DestroyRef);
  senderIDs: ISenderID[] = [];

  readonly formatCount = formatCount;
  private timeoutId: ReturnType<typeof setTimeout> | undefined;

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private route: ActivatedRoute
  ) {}

  messagesForm = this.fb.group({
    senderId: ['', Validators.required],
    subject: ['', Validators.required],
    body: ['', Validators.required],
    hashtags: this.fb.array([]),
    hashtag: '',
  });

  messageToEdit!: SavedMessages | undefined;
  messageId!: string;

  /** Length, parts and encoding of the copy as the network counts it. */
  get parts(): SmsParts {
    return smsParts(this.messagesForm.value.body as string);
  }

  /**
   * What the screen-reader note says about the cost. It changes only with the
   * part count or encoding, so it is not read out on every keystroke.
   */
  get partsAnnouncement(): string {
    const { parts, encoding } = this.parts;
    if (!parts) return '';
    return `${parts} SMS ${parts === 1 ? 'part' : 'parts'}${encoding === 'Unicode' ? ', sent as Unicode' : ''}`;
  }

  get unicodeNote(): string {
    const { encoding, offender } = this.parts;
    if (encoding !== 'Unicode') return '';
    const cause = offender ? `because of ${describeChar(offender)}` : 'because of a special character';
    return `Sent as Unicode ${cause}, so each part holds 70 characters instead of 160.`;
  }

  // get hashtags from form group
  get hashtags() {
    return this.messagesForm.get('hashtags') as FormArray;
  }

  // funtion to add hashtag to hashtag list
  addHashtag(hashtag: string) {
    this.hashtags.push(this.fb.control(hashtag));
  }

  setHashtags(tags?: string[]) {
    tags?.forEach((hashtag) => {
      this.hashtags.push(this.fb.control(hashtag));
    });
  }

  // function to remove hashtag from hashtag list
  removeHashtag(index: number) {
    this.hashtags.removeAt(index);
    this.messagesService.removeHashtag(index);
  }

  // function to get users input and add it to hashtag list.
  getHashtag(event: Event) {
    let hashtag = (event.target as HTMLInputElement).value.replace(/\s+/g, '');
    this.addHashtag(hashtag);
    this.messagesForm.controls['hashtag'].setValue('');
  }

  // function to add message attribute to message body...
  // appendAttribute(name: string) {
  //   this.messagesForm.controls['body'].setValue(
  //     this.messagesForm.value.body?.trimEnd() + ' ' + `{${name}}`
  //   );
  // }

  appendAttribute(textarea: HTMLTextAreaElement, attribute: string) {
    const template = `{{${attribute}}}`;

    const start = textarea.selectionStart; // cursor start
    const end = textarea.selectionEnd; // cursor end (same if no selection)

    // Build new string: before + template + after
    const messageBody = this.messagesForm.controls['body'].value || '';
    const before = messageBody.substring(0, start);
    const after = messageBody.substring(end);
    this.messagesForm.controls['body'].setValue(before + template + after);

    // Move cursor to the end of the inserted text
    const newPos = start + template.length;

    // DOM update is async – use setTimeout (or requestAnimationFrame)
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
    this.timeoutId = setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(newPos, newPos);
      this.timeoutId = undefined;
    }, 0);
  }

  // remove a contact-attribute placeholder from the message body (chip dismiss)
  removeAttribute(attribute: string) {
    this.messagesForm.controls['body'].setValue(
      removeMessageAttribute(this.messagesForm.controls['body'].value, attribute)
    );
  }

  //
  handleSaveMessage() {
    if (this.messagesForm.invalid) return;

    // replace message edited
    const index = this.messagesService.savedMessages.findIndex(
      (item) => item.id == this.messageId
    );
    if (index !== -1) {
      this.messagesService.savedMessages[index] = {
        id: this.messageId,
        subject: this.messagesForm.value.subject,
        body: this.messagesForm.value.body,
        hashtags: this.messagesForm.value.hashtags,
        senderId: this.messagesForm.value.senderId || '',
      };
    }

    this.messagesForm.reset();
    this.hashtags.clear();
    this.close();
  }

  // remove edit query param when form is closed
  removeQueryParam() {
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { edit: null }, // Set the param you want to remove to null
      queryParamsHandling: 'merge', // Merge with the existing query params
    });
  }

  // close popup
  close() {
    this.removeQueryParam();
    this.closePopupEvent.emit(false);
  }

  /** Previews (and test-sends) the copy as it stands in the editor, unsaved edits included. */
  previewMessage() {
    const value = this.messagesForm.value;
    this.messagesService.messages = {
      subject: value.subject ?? this.messageToEdit?.subject,
      body: value.body ?? this.messageToEdit?.body,
      hashtags: this.messageToEdit?.hashtags,
      senderId: value.senderId || this.messageToEdit?.senderId || '',
    };
    this.showTestMessage.emit(true);
  }

  ngOnInit() {
    // fetch sender IDs
    this.broadcastService.fetchSenderIDs().subscribe();
    this.broadcastService.getSenderIDs$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (value) => {
        this.senderIDs = value;
      },
    });

    // get message to edits id and fetch message from messaeg service to initialize form data
    this.route.queryParams.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      this.messageId = params['edit'];
      this.messageToEdit = this.messagesService.savedMessages.find(
        (item) => item.id == this.messageId
      );

      // Initialize form values
      this.messagesForm.patchValue({
        subject: this.messageToEdit?.subject,
        body: this.messageToEdit?.body,
        senderId: this.messageToEdit?.senderId,
      });

      // Initialize the hashtags FormArray
      this.hashtags.clear();
      this.setHashtags(this.messageToEdit?.hashtags);
    });
  }

  ngOnDestroy() {
    if (this.timeoutId) {
      clearTimeout(this.timeoutId);
    }
  }
}
